import { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Paper,
  Alert,
  LinearProgress,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import ImageHandler from '../../../../utils/ImageHandler';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { ImageCarouselCore } from '../../../../flow-core/components/ImageCarouselCore';

// Preview component for the carousel (needs hooks). Mirrors WhatsApp's render: swipeable image with
// small overlaid dots, no nav arrows and no meta caption, which the real flow doesn't show.
var ImageCarouselPreview = ({ properties }) => {
  var images = properties.images || [];
  var [currentIndex, setCurrentIndex] = useState(0);

  var aspectRatio = properties['aspect-ratio'] === '16:9' ? '56.25%' : '75%';
  var activeIndex = Math.min(currentIndex, Math.max(0, images.length - 1));

  return (
    <Box sx={{ opacity: properties.visible === false ? 0.5 : 1, mb: 2 }}>
      <Box sx={{ position: 'relative', paddingTop: aspectRatio, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
        {images.length > 0 && (
          <Box
            component="img"
            src={ImageHandler.toDisplayFormat(images[activeIndex]?.src)}
            alt={images[activeIndex]?.['alt-text'] || `Image ${activeIndex + 1}`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: properties['scale-type'] || 'contain',
            }}
          />
        )}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  bgcolor: idx === activeIndex ? 'common.white' : 'rgba(255,255,255,0.55)',
                  boxShadow: '0 0 2px rgba(0,0,0,0.45)',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Image upload component for carousel items
var CarouselImageEditor = ({ image, index, onChange, onRemove, canRemove }) => {
  var [uploadState, setUploadState] = useState({
    loading: false,
    error: null,
    progress: 0,
  });

  var handleFileUpload = async (file) => {
    setUploadState({ loading: true, error: null, progress: 0 });

    try {
      ImageHandler.validateFile(file);
      setUploadState((prev) => ({ ...prev, progress: 30 }));

      var rawBase64 = await ImageHandler.convertFile(file);
      setUploadState((prev) => ({ ...prev, progress: 80 }));

      onChange(index, 'src', rawBase64);
      setUploadState({ loading: false, error: null, progress: 100 });

      setTimeout(() => {
        setUploadState({ loading: false, error: null, progress: 0 });
      }, 1000);
    } catch (error) {
      setUploadState({ loading: false, error: error.message, progress: 0 });
    }
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Image {index + 1}</Typography>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          color="error"
          disabled={!canRemove}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {image.src && (
        <Paper sx={{ p: 1, mb: 1, textAlign: 'center' }}>
          <img
            src={ImageHandler.toDisplayFormat(image.src)}
            alt={image['alt-text'] || `Carousel image ${index + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100px',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={uploadState.loading ? <CircularProgress size={16} /> : <UploadIcon />}
          disabled={uploadState.loading}
          size="small"
        >
          {uploadState.loading ? 'Uploading...' : 'Upload'}
          <input
            type="file"
            hidden
            accept="image/jpeg,image/jpg,image/png"
            onChange={(e) => {
              var file = e.target.files[0];
              if (file) handleFileUpload(file);
            }}
          />
        </Button>
        <Typography variant="caption" color="text.secondary">
          JPEG, PNG (max 300KB)
        </Typography>
      </Box>

      {uploadState.loading && (
        <LinearProgress
          variant="determinate"
          value={uploadState.progress}
          sx={{ mb: 1, height: 4, borderRadius: 2 }}
        />
      )}

      {uploadState.error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {uploadState.error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Alt Text"
        value={image['alt-text'] || ''}
        onChange={(e) => onChange(index, 'alt-text', e.target.value)}
        size="small"
        helperText="Accessibility description for the image"
      />
    </Box>
  );
};

export const ImageCarouselComponent = new FlowComponent({
  ...ImageCarouselCore,
  renderPreview(properties) {
    return <ImageCarouselPreview properties={properties} />;
  },
  renderPropertyEditor(properties, onChange) {
    var handleImageChange = (index, field, value) => {
      var currentImages = properties.images || [];
      var newImages = [...currentImages];
      newImages[index] = { ...newImages[index], [field]: value };
      onChange('images', newImages);
    };

    var handleAddImage = () => {
      var currentImages = properties.images || [];
      if (currentImages.length < 3) {
        // Default alt-text to "Image X" (X = the new image's position) so it's valid out of the box
        //, WhatsApp requires alt-text on every carousel image.
        onChange('images', [...currentImages, { src: '', 'alt-text': `Image ${currentImages.length + 1}` }]);
      }
    };

    var handleRemoveImage = (index) => {
      var currentImages = properties.images || [];
      if (currentImages.length > 1) {
        onChange('images', currentImages.filter((_, i) => i !== index));
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          ImageCarousel supports 1-3 images. Maximum 2 carousels per screen, 3 per flow.
        </Alert>

        <FormControl fullWidth size="small">
          <InputLabel>Aspect Ratio</InputLabel>
          <Select
            value={properties['aspect-ratio'] || '4:3'}
            onChange={(e) => onChange('aspect-ratio', e.target.value)}
            label="Aspect Ratio"
          >
            <MenuItem value="4:3">4:3</MenuItem>
            <MenuItem value="16:9">16:9</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Scale Type</InputLabel>
          <Select
            value={properties['scale-type'] || 'contain'}
            onChange={(e) => onChange('scale-type', e.target.value)}
            label="Scale Type"
          >
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="cover">Cover</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Images ({(properties.images || []).length}/3)
        </Typography>

        {(properties.images || []).map((image, index) => (
          <CarouselImageEditor
            key={index}
            image={image}
            index={index}
            onChange={handleImageChange}
            onRemove={handleRemoveImage}
            canRemove={(properties.images || []).length > 1}
          />
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddImage}
          variant="outlined"
          disabled={(properties.images || []).length >= 3}
        >
          Add Image
        </Button>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Visible
          </Typography>
          <input
            type="checkbox"
            checked={properties.visible !== false}
            onChange={(e) => onChange('visible', e.target.checked)}
          />
        </Box>
      </Box>
    );
  },
});
